import { useEffect } from 'react';

/**
 * Show the browser's native "leave this page?" prompt when the user
 * tries to close the tab / refresh / hit back with unsaved changes.
 *
 * Pass Formik's `dirty` (or any boolean) — when true, a `beforeunload`
 * handler is wired up; when false, it's torn down. The browser shows
 * its native confirm; modern Chromium / Firefox / Safari all ignore
 * the custom message string but still prompt as long as we call
 * `event.preventDefault()` and assign `event.returnValue`.
 *
 * Scope note: this protects against tab close / refresh / external
 * navigation only. Intercepting in-app React Router navigation needs
 * `useBlocker` from the data-router APIs, which require `RouterProvider`
 * and aren't available in this app's declarative `BrowserRouter` setup.
 * Each form's explicit "Cancel" button stays the way to bail out without
 * a save.
 */
const useUnsavedChangesGuard = (dirty) => {
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      // Required by older Chrome/Edge so the prompt fires. Modern
      // browsers ignore the value but still need it set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
};

export default useUnsavedChangesGuard;
